/* Per-client portal customisation card.
 *
 * Firm admins pick a preset (minimal / standard / custom) plus optional
 * individual toggles. Sends PUT /api/clients/<id>/portal-features on save.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw, Eye, EyeOff, Sparkles, LayoutDashboard, Wrench, FlaskConical, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

// Where each toggle opens when previewing "as the client".
const PREVIEW_ROUTES = {
    messages: "/portal/messages",
    upload: "/portal/upload",
    tax_position: "/portal/tax-position",
    tax_returns: "/portal/returns",
    compliance: "/portal/compliance",
    requests: "/portal/requests",
    history: "/portal/history",
    help: "/portal/help",
    hero_snapshot: "/portal",
    action_claim: "/portal/claim",
    action_afford: "/portal/afford",
    action_ask: "/portal/ask",
    action_pay_self: "/portal/pay-self",
    action_hire: "/portal/hire",
    action_price_change: "/portal/price-change",
    action_growth_plan: "/portal/growth-plan",
    action_wealth_plan: "/portal/wealth-plan",
    action_break_even: "/portal/break-even",
    trust_strip: "/portal",
};

const KEY_LABELS = {
    // Nav tabs
    messages: { label: "Messages", hint: "Chat with the firm", group: "Nav tabs" },
    upload: { label: "Upload tab", hint: "Dedicated screen for sending files. Turn off if the client only uses the Home Upload button.", group: "Nav tabs" },
    tax_position: { label: "Tax position / Insights", hint: "Estimated tax owed and GST position", group: "Nav tabs" },
    tax_returns: { label: "Tax returns", hint: "Signed IR3 / IR4 / GST returns list", group: "Nav tabs" },
    compliance: { label: "Compliance", hint: "Engagement letter, IRD forms", group: "Nav tabs" },
    requests: { label: "Requests", hint: "Data requests sent by the firm", group: "Nav tabs" },
    history: { label: "History / Documents", hint: "Full activity log", group: "Nav tabs" },
    help: { label: "Help", hint: "Support articles and contact info", group: "Nav tabs" },
    // Home-screen sections (Feb 2026 redesign)
    hero_snapshot: { label: "Home hero cards", hint: "Safe to spend / Tax reserve / GST position / Bank balance + freshness pill + GST banner", group: "Home screen" },
    action_claim: { label: "Card: Can I claim it?", hint: "Green gradient card — tax-deductibility helper", group: "Home screen" },
    action_afford: { label: "Card: Can I afford it?", hint: "Blue gradient card — purchase impact scenario", group: "Home screen" },
    action_ask: { label: "Card: Ask your accountant", hint: "Purple gradient card — AI answer + escalation to human", group: "Home screen" },
    // Money-question doorways (Aug 2026) — deterministic answers driven by
    // the client's Xero/Prosaic snapshot. Firm can toggle per-client.
    action_pay_self: { label: "Card: How much can I pay myself?", hint: "Sustainable owner draw based on income, expenses, and tax reserve", group: "Money questions" },
    action_hire: { label: "Card: Can I afford to hire?", hint: "Impact of adding a new staff salary against current profit", group: "Money questions" },
    action_price_change: { label: "Card: What if I raised my prices?", hint: "Revenue and margin lift from a price change", group: "Money questions" },
    action_growth_plan: { label: "Card: Growth plan", hint: "Next year's revenue target and monthly milestones", group: "Money questions" },
    action_wealth_plan: { label: "Card: Grow my wealth", hint: "Long-term wealth projection (property/shares). Also lives at the public /tools/wealth-plan URL — share it on socials to draw people in", group: "Money questions" },
    action_break_even: { label: "Card: Sales target & break-even", hint: "How much sales do I need to cover costs? Plain-English answer + daily/weekly targets + optional target profit. Uses trailing 3 months from Prosaic/Xero.", group: "Money questions" },
    trust_strip: { label: "Trust strip", hint: "Bottom row: Secure · Built for your business · Actionable", group: "Home screen" },
};

const PRESET_CARDS = [
    {
        key: "testing", label: "Testing", icon: FlaskConical,
        blurb: "Only Cash Register + Upload + Messages visible. Perfect for pilot clients while the rest of the app is still being built.",
    },
    {
        key: "minimal", label: "Minimal", icon: Sparkles,
        blurb: "Just Home + Upload + Help. Great for clients who feel overwhelmed and only need to send bank statements.",
    },
    {
        key: "standard", label: "Standard", icon: LayoutDashboard,
        blurb: "Everything visible. Best for confident, tech-savvy clients who want full access.",
    },
    {
        key: "custom", label: "Custom", icon: Wrench,
        blurb: "Pick each feature individually below.",
    },
];

export default function PortalFeaturesCard({ clientId, clientName }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "firm_admin";
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [preset, setPreset] = useState("standard");
    const [features, setFeatures] = useState({});
    const [available, setAvailable] = useState([]);
    const [presetsDefs, setPresetsDefs] = useState({});
    const [initial, setInitial] = useState({ preset: "standard", features: {} });

    useEffect(() => { load(); }, [clientId]);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get(`/clients/${clientId}/portal-features`);
            setPreset(data.preset || "standard");
            setFeatures(data.features || {});
            setAvailable(data.available_keys || []);
            setPresetsDefs(data.presets || {});
            setInitial({ preset: data.preset || "standard", features: data.features || {} });
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setLoading(false); }
    }

    // The map that would be sent to the portal right now (before save).
    const resolved = useMemo(() => {
        const base = Object.fromEntries(available.map((k) => [k, true]));
        if (preset && presetsDefs[preset]) Object.assign(base, presetsDefs[preset]);
        if (preset === "custom") Object.assign(base, features);
        return base;
    }, [available, preset, features, presetsDefs]);

    const dirty = useMemo(() => {
        if (preset !== initial.preset) return true;
        const keys = new Set([...Object.keys(features || {}), ...Object.keys(initial.features || {})]);
        for (const k of keys) if ((features?.[k] ?? null) !== (initial.features?.[k] ?? null)) return true;
        return false;
    }, [preset, features, initial]);

    async function save() {
        setSaving(true);
        try {
            await api.put(`/clients/${clientId}/portal-features`, {
                preset,
                features: preset === "custom" ? features : null,
            });
            toast.success("Portal features updated");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setSaving(false); }
    }

    function revert() { setPreset(initial.preset); setFeatures(initial.features || {}); }

    function toggle(k) {
        // Toggling any individual switch implicitly moves the client to 'custom'
        // so future preset shifts don't clobber their preference.
        const current = resolved[k];
        const next = { ...(preset === "custom" ? features : resolved), [k]: !current };
        setPreset("custom");
        setFeatures(next);
    }

    if (loading) return <div className="co-card p-6 flex items-center gap-2 text-sm text-[#5E636E]"><Loader2 className="h-4 w-4 animate-spin" /> Loading portal preferences…</div>;

    return (
        <div className="co-card overflow-hidden" data-testid="portal-features-card">
            <div className="px-6 py-4 border-b border-[#E4E4E7]">
                <h3 className="font-display text-lg font-medium">Portal features for {clientName || "this client"}</h3>
                <p className="mt-1 text-sm text-[#5E636E]">
                    Control which top-level tabs {clientName ? clientName : "this client"} sees when they sign in. Home is always visible. Overwhelmed clients often work best on <span className="font-medium">Minimal</span>.
                </p>
            </div>

            <div className="px-6 py-4 grid gap-3 md:grid-cols-4">
                {PRESET_CARDS.map((p) => {
                    const active = preset === p.key;
                    return (
                        <button
                            key={p.key}
                            type="button"
                            onClick={() => setPreset(p.key)}
                            disabled={!isAdmin}
                            className={`text-left rounded-lg border p-3 transition-colors ${active ? "border-[#0E3B2E] bg-[#F5F4EF] ring-1 ring-[#0E3B2E]" : "border-[#E4E4E7] hover:border-[#0E3B2E]"}`}
                            data-testid={`portal-preset-${p.key}`}
                        >
                            <div className="flex items-center gap-2">
                                <p.icon className="h-4 w-4 text-[#0E3B2E]" />
                                <div className="font-medium text-sm">{p.label}</div>
                            </div>
                            <p className="mt-1.5 text-[11px] leading-snug text-[#5E636E]">{p.blurb}</p>
                        </button>
                    );
                })}
            </div>

            <div className="px-6 pb-4">
                {/* Iterate group names dynamically from KEY_LABELS so a new
                    feature group added to the map surfaces automatically. */}
                {Array.from(new Set(Object.values(KEY_LABELS).map((v) => v.group))).map((groupName) => {
                    const rows = available.filter((k) => (KEY_LABELS[k]?.group) === groupName);
                    if (rows.length === 0) return null;
                    return (
                        <div key={groupName} className="mt-3 first:mt-0">
                            <div className="co-label mb-1.5" data-testid={`portal-feature-group-${groupName.toLowerCase().replaceAll(" ", "-")}`}>{groupName}</div>
                            <div className="rounded-lg border border-[#E4E4E7] divide-y divide-[#E4E4E7] overflow-hidden">
                                {rows.map((k) => {
                                    const on = resolved[k];
                                    const meta = KEY_LABELS[k] || { label: k, hint: "" };
                                    return (
                                        <div key={k} className="flex items-start justify-between gap-4 px-4 py-3" data-testid={`portal-feature-row-${k}`}>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    {on ? <Eye className="h-3.5 w-3.5 text-emerald-700" /> : <EyeOff className="h-3.5 w-3.5 text-[#A1A1AA]" />}
                                                    <Label htmlFor={`pf-${k}`} className="text-sm font-medium cursor-pointer">{meta.label}</Label>
                                                </div>
                                                {meta.hint && <p className="mt-0.5 text-[11px] text-[#5E636E] ml-5">{meta.hint}</p>}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {PREVIEW_ROUTES[k] && (
                                                    <button
                                                        type="button"
                                                        onClick={() => window.open(`${PREVIEW_ROUTES[k]}?preview_client_id=${clientId}`, "_blank", "noopener")}
                                                        className="inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] bg-white px-2 py-1 text-[11px] font-medium text-[#0F1115] hover:bg-[#FAFAF7]"
                                                        title="Preview as this client (opens in a new tab)"
                                                        data-testid={`portal-feature-preview-${k}`}
                                                    >
                                                        <ExternalLink className="h-3 w-3" /> Preview
                                                    </button>
                                                )}
                                                <Switch
                                                    id={`pf-${k}`}
                                                    checked={!!on}
                                                    onCheckedChange={() => toggle(k)}
                                                    disabled={!isAdmin || saving}
                                                    data-testid={`portal-feature-toggle-${k}`}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
                {/* Any keys the backend adds later that aren't in KEY_LABELS map — show at the bottom so nothing is missed */}
                {(() => {
                    const known = new Set(Object.keys(KEY_LABELS));
                    const orphans = available.filter((k) => !known.has(k));
                    if (orphans.length === 0) return null;
                    return (
                        <div className="mt-3">
                            <div className="co-label mb-1.5">Other</div>
                            <div className="rounded-lg border border-[#E4E4E7] divide-y divide-[#E4E4E7] overflow-hidden">
                                {orphans.map((k) => {
                                    const on = resolved[k];
                                    return (
                                        <div key={k} className="flex items-start justify-between gap-4 px-4 py-3">
                                            <Label htmlFor={`pf-${k}`} className="text-sm font-medium cursor-pointer">{k}</Label>
                                            <Switch id={`pf-${k}`} checked={!!on} onCheckedChange={() => toggle(k)} disabled={!isAdmin || saving} />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })()}
                <p className="mt-3 text-[11px] text-[#5E636E]">
                    Home (the greeting) and the required data-source (Prosaic / Xero) gate are always visible.
                    Billable add-ons (Cash Register, Morning Brief, Spend Insights, Job Odometer) have their own toggles further down this page.
                </p>
            </div>

            <div className="border-t border-[#E4E4E7] px-6 py-3 flex items-center gap-2 bg-[#FAFAFA]">
                <Button onClick={save} disabled={!isAdmin || saving || !dirty} className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white" data-testid="portal-features-save">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
                </Button>
                <Button variant="outline" onClick={revert} disabled={saving || !dirty} data-testid="portal-features-revert">
                    <RotateCcw className="h-4 w-4" /> Revert
                </Button>
                {!dirty && !loading && <span className="ml-2 text-xs text-[#5E636E]">All saved.</span>}
            </div>
        </div>
    );
}
